import { defineModule } from '../../engine/module';
import { InviteAction, PortalInviteWidget } from './InviteToPortal';

// The STAFF side of the customer portal (docs/modules/portal.md): a "Portal" group under the
// Workspace nav — portal users (invite / resend / disable / enable / reset), portal roles
// (capability objects), menu buttons and the per-tenant settings row. Every screen is an engine
// ScreenDef over the portal-* resources; the only custom piece is the invite dialog (a
// collection action + the `portal-invite` field widget). The customer-facing SPA lives in
// web/src/portal and is routed from App.tsx (/portal/*), outside the staff shell.

export const portalModule = defineModule({
  name: 'portal',
  nav: [
    {
      label: 'Portal',
      area: 'config',
      order: 70,
      items: [
        { label: 'Portal users', to: '/portal-users', icon: 'globe' },
        { label: 'Portal roles', to: '/portal-roles', icon: 'shield-check' },
        { label: 'Portal menu', to: '/portal-menu-buttons', icon: 'layout-dashboard' },
        { label: 'Portal settings', to: '/portal-settings', icon: 'sliders-horizontal' },
      ],
    },
  ],
  screens: [
    {
      path: '/portal-users',
      resource: 'portal-users',
      kind: 'list',
      title: 'Portal users',
      subtitle: 'Customer contacts who can sign in to the portal',
      columns: ['email', 'contact_id', 'status', 'portal_role_id', 'invited_at', 'activated_at', 'last_login_at'],
      filters: [{ field: 'status', kind: 'chips' }],
      query: { sort: '-created_at', expand: ['contact', 'portal_role'] },
      create: false,
      actions: [{ name: 'invite', label: 'Invite contact', scope: 'collection', component: InviteAction, placement: 'primary', openParam: 'invite' }],
    },
    {
      path: '/portal-users/:id',
      resource: 'portal-users',
      kind: 'detail',
      builtinTabs: ['overview', 'activity'],
      actions: [
        { name: 'reinvite', when: { status: ['invited', 'active'] } },
        { name: 'disable', when: { status: ['invited', 'active'] } },
        { name: 'enable', when: { status: ['disabled'] } },
        { name: 'reset', when: { status: ['active'] } },
        { name: 'invite', hidden: true },
      ],
    },
    {
      path: '/portal-roles',
      resource: 'portal-roles',
      kind: 'list',
      title: 'Portal roles',
      subtitle: 'What each kind of customer user may do in the portal',
      columns: ['name', 'key', 'is_default', 'is_system', 'sort'],
      query: { sort: 'sort' },
      form: { fields: ['key', 'name', 'description', 'is_default', 'sort', 'capabilities'] },
    },
    { path: '/portal-roles/:id', resource: 'portal-roles', kind: 'detail', builtinTabs: ['overview', 'activity'] },
    {
      path: '/portal-menu-buttons',
      resource: 'portal-menu-buttons',
      kind: 'list',
      title: 'Portal menu',
      subtitle: 'The tiles and navigation customers see, in order',
      columns: ['sort', 'label', 'function', 'route', 'icon', 'show_on_home', 'show_in_nav', 'is_active'],
      query: { sort: 'sort' },
      form: { fields: ['key', 'label', 'description', 'function', 'route', 'icon', 'colour', 'sort', 'show_on_home', 'show_in_nav', 'is_active'] },
    },
    { path: '/portal-menu-buttons/:id', resource: 'portal-menu-buttons', kind: 'detail', builtinTabs: ['overview', 'activity'] },
    {
      path: '/portal-settings',
      resource: 'portal-settings',
      kind: 'list',
      title: 'Portal settings',
      subtitle: 'Branding, anonymous logging, signed-link policy — open the row to edit',
      columns: ['brand_name', 'anonymous_logging', 'require_login_for_links', 'allow_reopen', 'allow_close', 'attachments_enabled', 'support_email'],
      create: false,
      detailPath: '/portal-settings/:id',
    },
    { path: '/portal-settings/:id', resource: 'portal-settings', kind: 'detail', title: 'Portal settings', builtinTabs: ['overview'] },
  ],
  widgets: { 'portal-invite': PortalInviteWidget },
  commands: [
    { id: 'portal.invite', label: 'Invite a contact to the customer portal', kind: 'create', icon: 'user-plus', keywords: 'portal invite customer', run: ({ navigate }) => navigate('/portal-users?invite=1'), permission: 'portal-users:invite' },
    { id: 'portal.open', label: 'Open the customer portal', kind: 'go to', icon: 'globe', keywords: 'portal customer', run: () => window.open('/portal', '_blank', 'noopener') },
  ],
});
